// Library methods
import { useMemo } from "react";

import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";

// Styles
import { ModalCustomCancel } from "src/styles/mui/muiStylesHelper";

// Images
import picoController from "../../../../../../assets/images/update-device-modal/PicoController.jpeg";
import arborWifiConnected from "../../../../../../assets/images/update-device-modal/ArborWiFiConnected.jpeg";
import arborExitWifi from "../../../../../../assets/images/update-device-modal/ArborExitWiFi.jpeg";
import retinalogikAssignment from "../../../../../../assets/images/update-device-modal/RetinaLogikAssignmentUI.jpeg";
import arborLobbyProd from "../../../../../../assets/images/update-device-modal/ArborLobbyProd.jpeg";

type UpdateDeviceModalType = {
  open: boolean;
  onClose: () => void;
};

const UpdateDeviceModal = ({ open, onClose }: UpdateDeviceModalType) => {
  const { t } = useTranslation();

  const updateInstructions = useMemo(
    () => [
      {
        header: "1",
        text: t(
          "Press the Pico button on your controller to open the ArborXR 'Quick Access' panel."
        ),
        img: picoController,
      },
      {
        header: "2",
        text: t("Make sure the headset is connected to Wi-Fi."),
        img: arborWifiConnected,
      },
      {
        header: "2a",
        text: t(
          "To return to the 'Quick Access' panel, click the 'Resume App' button and repeat step 1."
        ),
        img: arborExitWifi,
      },
      {
        header: "3",
        text: t(
          "Wait 5-10 minutes for the update to complete. You do not need to be wearing the headset during this time."
        ),
        img: null,
      },
      {
        header: "4",
        text: t(
          "Verify that the RetinaLogik version has been updated. The version can be found above the app icon in ArborXR."
        ),
        img: arborLobbyProd,
      },
      {
        header: "",
        text: t(
          "and in the bottom right corner of most panels within the app itself."
        ),
        img: retinalogikAssignment,
      },
      {
        header: "5",
        text: t(
          "If the version has not been updated, restart the device and repeat the above steps."
        ),
        img: null,
      },
    ],
    [t]
  );

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("How to Update Your Device")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={onClose}>
            {t("Close")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="16px" gap="20px">
        {updateInstructions.map((instruction, index) => (
          <Box
            key={index}
            style={{
              width: "80%",
              display: "flex",
              flexDirection: "column",
              margin: "0 auto",
              gap: "20px",
            }}
          >
            {instruction.img && (
              <Box>
                <img
                  width="100%"
                  src={instruction.img}
                  alt={instruction.text}
                  style={{ borderRadius: "8px", maxHeight: "300px" }}
                />
              </Box>
            )}
            <Typography
              id={`update-modal-description-${index}`}
              fontSize="14px"
            >
              {instruction.header && (
                <span style={{ fontWeight: 600 }}>{instruction.header}. </span>
              )}
              {instruction.text}
            </Typography>
          </Box>
        ))}
      </Grid>
    </DefaultModal>
  );
};

export default UpdateDeviceModal;
